import { useEffect } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'

const HeroMedia = dynamic(() => import('../components').then(mod => mod.HeroMedia))
const SectionItems = dynamic(() => import('../components').then(mod => mod.SectionItems))
const SectionSlider = dynamic(() => import('../components').then(mod => mod.SectionSlider))
const MainContainer = dynamic(() => import('../components').then(mod => mod.MainContainer))

import { isObjectEmpty } from '../utils'
import { PAGE_MEDIA } from '../services/queries/pages'
import getGlobalData from '../services/cacheGlobalData'
import { STATIC_PAGE_WP_IDS, CATEGORY_NEWS_SLUGS } from '../utils/siteConfig'
import { fetchPageByGql } from '../services'
import { scrollToTargetAdjusted, formatDate } from '../utils'

/**
 * Signature Media Page
 */
export default function Media({ pageData, communityNewsSliderData, pressNewsSliderData }) {
  const router = useRouter()

  useEffect(() => {
    if (process?.browser) {
      // detect ID in url & then scroll to
      let targetElementID = router?.asPath?.split('#')?.[1]
      if (targetElementID) {
        scrollToTargetAdjusted(targetElementID)
      }
    }
  }, [])

  // Page Data
  const header = pageData?.signaturePageMediaFields?.header
  const awards = pageData?.signaturePageMediaFields?.awards
  const books = pageData?.signaturePageMediaFields?.books
  const inThePress = pageData?.signaturePageMediaFields?.inThePress
  const magazineFeatures = pageData?.signaturePageMediaFields?.magazineFeatures

  const magazinesArray = magazineFeatures?.magazines?.map(item => {
    return {
      image: {
        sourceUrl: item?.image?.sourceUrl,
        altText: item?.image?.altText,
      },
      heading: item?.title,
      subHeading: item?.headline,
      externalUrl: item?.externalLink,
      description: item?.description,
      cta: item?.externalLink ? {
        label: 'Read More',
        type: 'external',
        url: item?.externalLink,
      } : null,
    }
  })

  const awardsArray = awards?.awards?.map(item => {
    return {
      heading: item?.title,
      description: item?.subtitle,
      date: item?.date,
      externalUrl: item?.externalLink,
      image: {
        sourceUrl: item?.image?.sourceUrl,
        altText: item?.image?.altText,
      },
    }
  })

  const booksArray = books?.books?.map(item => {
    const cta = item?.externalLink
      ? {
          label: 'Get the book',
          type: 'external',
          externalUrl: item?.externalLink,
        }
      : null

    return {
      image: {
        sourceUrl: item?.image?.sourceUrl,
        altText: item?.image?.altText,
      },
      heading: item?.title,
      subHeading: item?.headline,
      description: item?.description,
      cta: cta,
    }
  })

  const newsArray = pressNewsSliderData?.map(item => {
    // Get description from Headline
    const contentBuilder = item?.signaturePostNewsFields?.contentBuilder
    const filteredHeadline = contentBuilder?.filter(item => {
      return item.text
    })
    const description = filteredHeadline.length > 0 ? filteredHeadline[0].text : ''

    return {
      image: {
        sourceUrl: item?.signaturePostNewsFields?.headerImage?.sourceUrl,
        altText: item?.signaturePostNewsFields?.headerImage?.altText,
      },
      heading: item?.title,
      subHeading: formatDate(item?.date),
      description: description,
      cta: {
        label: 'Read More',
        type: 'internal',
        url: item?.uri,
      },
    }
  })

  return (
    <MainContainer>
      {/* SECTION - HERO - w/MediaText */}
      {/* <HeroMedia disableCopy={true} heroHeading={header?.title} headerTitleIndent='56' /> */}

      {/* Section A - Magazine Features */}
      <SectionSlider
        id='magazines'
        className='pb-80 md:pb-60 hero-pt mb-60'
        heading={magazineFeatures?.title}
        // headingExtraIndent={true}
        subHeading={magazineFeatures?.headline}        
        sliderCards={magazinesArray}
        imageAspectRatio='aspect-w-11 aspect-h-13.5'
        sliderCardClassName='md:min-w-80vw md:min-w-346 md:max-w-80vw md:max-w-346'
        // sectionCta={{
        //   type: 'internal',
        //   url: '/magazines',
        //   label: 'All Magazine Features',
        //   buttonColor: 'none',
        //   fullWidthMobile: true,
        // }}
        titleOnScrollAnimationDelay={1.5}
      />
    </MainContainer>
  )
}

export async function getStaticProps({ preview = false }) {
  const query = PAGE_MEDIA
  const params = {
    pageId: STATIC_PAGE_WP_IDS?.media,
    pressTerm: CATEGORY_NEWS_SLUGS.news,
  }

  const allMediaData = await fetchPageByGql(query, params, preview)

  const pageData = allMediaData?.pageData
  const pressNewsSliderData = allMediaData?.latestPressNews?.nodes

  const globalData = await getGlobalData()

  return isObjectEmpty(pageData)
    ? { notFound: true } // returns custom 404 page
    : {
        props: {
          preview,
          pageData,
          globalData,
          pressNewsSliderData,
          additionalHeaderInfo: {
            pageType: 'media', // fixed
            text: 'Back to All Media', // Customizable
            href: '/media', // slug - customizable
          },
        },
      }
}
